/**
 * Chat Refinement Panel — AI chat sidebar for iterating on the website.
 */

import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Send, Loader2, Sparkles, Bot, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

interface ChatRefinementProps {
  projectId: string;
  jobId: string;
  onContentUpdated: () => void;
  onClose: () => void;
}

interface ChatMsg {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  edits?: Array<{ type: string; target?: string; description: string }>;
}

export function ChatRefinement({
  projectId,
  jobId,
  onContentUpdated,
  onClose,
}: ChatRefinementProps) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();

  // Fetch chat history
  const { data: history } = useQuery({
    queryKey: ['editor-chat', projectId, jobId],
    queryFn: async () => {
      const res = await api.get(`/editor/${projectId}/${jobId}/chat/history`);
      return (res.data.data || []) as ChatMsg[];
    },
  });

  // Send message
  const sendMessage = useMutation({
    mutationFn: async (message: string) => {
      const res = await api.post(`/editor/${projectId}/${jobId}/chat`, { message });
      return res.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['editor-chat', projectId, jobId] });
      if (data.contentUpdated || data.pagesGenerated) {
        onContentUpdated();
      }
    },
    onError: () => toast.error('Failed to send message'),
  });

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, sendMessage.isPending]);

  const handleSend = () => {
    const msg = input.trim();
    if (!msg || sendMessage.isPending) return;
    setInput('');
    sendMessage.mutate(msg);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestions = [
    'Make the design more modern and bold',
    'Change colors to a dark theme',
    'Add a testimonials section',
    'Generate the missing About and Contact pages',
    'Make it more professional',
  ];

  return (
    <div className="flex w-80 flex-col border-l bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold">AI Assistant</span>
        </div>
        <button onClick={onClose} className="rounded p-1 hover:bg-gray-100">
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {/* Welcome message */}
        {(!history || history.length === 0) && !sendMessage.isPending && (
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                <Bot className="h-3.5 w-3.5 text-blue-600" />
              </div>
              <p className="text-sm leading-relaxed text-gray-600">
                Hi! I can help you refine your website. Try things like changing colors, adding
                sections, or updating content.
              </p>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-400">Suggestions</p>
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setInput(s);
                    inputRef.current?.focus();
                  }}
                  className="block w-full rounded-md border px-3 py-2 text-left text-xs text-gray-600 transition-colors hover:border-blue-200 hover:bg-blue-50"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chat messages */}
        {history?.map((msg, i) => (
          <div key={msg.id || i} className="flex items-start gap-2">
            <div
              className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
                msg.role === 'user' ? 'bg-gray-100' : 'bg-blue-100'
              }`}
            >
              {msg.role === 'user' ? (
                <User className="h-3.5 w-3.5 text-gray-500" />
              ) : (
                <Bot className="h-3.5 w-3.5 text-blue-600" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                {msg.content}
              </p>
              {msg.edits && msg.edits.length > 0 && (
                <div className="mt-1.5 space-y-1">
                  {msg.edits.map((edit, j) => (
                    <div
                      key={j}
                      className="flex items-center gap-1.5 rounded bg-blue-50 px-2 py-1 text-xs text-blue-600"
                    >
                      <Sparkles className="h-3 w-3" />
                      {edit.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {sendMessage.isPending && (
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
              <Bot className="h-3.5 w-3.5 text-blue-600" />
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-400">
              <Loader2 className="h-3 w-3 animate-spin" />
              Thinking...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-3">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe changes..."
            rows={1}
            className="flex-1 resize-none rounded-lg border bg-gray-50 px-3 py-2 text-sm placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
            style={{ maxHeight: '100px' }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sendMessage.isPending}
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
